import {SearchOutlined} from '@ant-design/icons';
import {Button, Input, InputRef} from 'antd';
import {ColumnProps} from 'antd/lib/table';
import {getIn} from 'formik';
import React, {useRef, useState} from 'react';
import Highlighter from 'react-highlight-words';

interface IFilterDropdownParams {
  selectedKeys: string[];
  setSelectedKeys(keys: string[]): void;
  confirm(): void;
  clearFilters(): void;
}

interface IProps<T> extends ColumnProps<T> {
  dataIndex: string;
  title: string;
}

export function useTableSearch<T extends {[key: string]: any}>(columnProps: IProps<T>): IProps<T> {
  const [searchText, onSearch] = useState('');
  const searchInput = useRef<InputRef>(null);
  const name = columnProps.title || columnProps.dataIndex;

  const handleSearch = (selectedKeys: string[], confirm: () => void) => {
    onSearch(selectedKeys[0]);
    confirm();
  };

  const handleReset = (clearFilters: () => void, confirm: () => void) => {
    clearFilters();
    confirm();
    onSearch('');
  };

  return {
    ...columnProps,
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters
    }: IFilterDropdownParams) => (
      <div style={{padding: 8}}>
        <Input
          ref={searchInput}
          placeholder={`Search by ${name.toLowerCase()}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm)}
          style={{width: 250, marginBottom: 8, display: 'block'}}
        />
        <Button
          type="primary"
          onClick={() => handleSearch(selectedKeys, confirm)}
          icon={<SearchOutlined />}
          size="small"
          style={{width: 120, marginRight: 10}}
          htmlType="button">
          Search
        </Button>
        <Button
          onClick={() => handleReset(clearFilters, confirm)}
          size="small"
          style={{width: 120}}
          htmlType="button">
          Reset
        </Button>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{fontSize: 14, color: filtered ? '#1c9aae' : undefined}} />
    ),
    onFilter: (value: any, record: T) =>
      getIn(record, columnProps.dataIndex, '')
        .toString()
        .toLowerCase()
        .includes(value.toLowerCase()),
    onFilterDropdownVisibleChange: (visible: boolean) => {
      if (visible) {
        setTimeout(() => {
          if (!searchInput.current) return;
          return searchInput.current.select();
        });
      }
    },
    render: (text: string, record: T, index: number) => {
      const highlighted = (
        <Highlighter
          highlightStyle={{backgroundColor: 'antiquewhite', padding: 0}}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text.toString()}
        />
      );
      if (columnProps.render) return columnProps.render(highlighted, record, index);
      return highlighted;
    }
  };
}
